import React, { useEffect, useMemo, useState } from 'react';
import { Container, Button } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheckCircle } from 'react-icons/fi';
import styles from '../components/bodywon.module.css';
import { normalizeAddress } from './addressHelpers';
import DeliveryAddressCard from './DeliveryAddressCard';
import { fetchAddressById } from '../utils/addressApi';
import { fetchCartDetailById, ensureLoggedInUserId, isCartUserLoggedIn } from '../utils/cartApi';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { getImageSrc } from '../utils/productStorefront';
import { calculateLineTotal, calculateOrderTotals } from '../utils/cartTotals';
import OrderTotalsSummary from '../components/OrderTotalsSummary';

const OrderConfirmPage = ({ onRequireLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cartId = searchParams.get('cartId');
  const addressId = searchParams.get('addressId');
  const orderId = searchParams.get('orderId');
  const orderNumber = searchParams.get('orderNumber');
  const { formatPrice, settings } = useSettings();
  const { refreshCart } = useCart();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [address, setAddress] = useState(null);
  const [cart, setCart] = useState(null);

  useEffect(() => {
    if (!isCartUserLoggedIn()) {
      onRequireLogin?.();
      navigate('/', { replace: true });
      return;
    }
    if (!cartId || !addressId) {
      setError('Order reference missing.');
      setLoading(false);
      return;
    }

    let active = true;

    refreshCart({ showLoading: false });

    (async () => {
      try {
        const userId = await ensureLoggedInUserId();
        const [addressData, cartData] = await Promise.all([
          fetchAddressById(addressId),
          fetchCartDetailById(cartId, userId),
        ]);
        if (!active) return;
        setAddress(normalizeAddress(addressData));
        setCart(cartData);
      } catch (err) {
        if (active) setError(err.message || 'Could not load order.');
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [addressId, cartId, navigate, onRequireLogin, refreshCart]);

  const items = cart?.items ?? [];
  const totals = useMemo(
    () => calculateOrderTotals(
      items.map((item) => ({ price: item.price, quantity: item.quantity })),
      settings,
      address
    ),
    [items, settings, address]
  );

  if (!isCartUserLoggedIn()) return null;

  return (
    <section className={styles.checkoutAddressPage}>
      <Container>
        <div className={styles.orderConfirmHero}>
          <FiCheckCircle size={56} className={styles.orderConfirmIcon} />
          <h1 className={styles.checkoutAddressTitle}>Order Placed Successfully</h1>
          <p className={styles.checkoutAddressSub}>
            Thank you! Your order{' '}
            <strong>{orderNumber || (orderId ? `#${orderId}` : '')}</strong>{' '}
            has been confirmed.
          </p>
        </div>

        {loading ? (
          <div className={styles.checkoutAddressCard}>Loading order summary...</div>
        ) : error ? (
          <div className={styles.checkoutAddressCard}>
            <div className={styles.checkoutAddressError}>{error}</div>
          </div>
        ) : (
          <div className={styles.orderReviewLayout}>
            <div>
              <div className={styles.checkoutAddressCard} style={{ marginBottom: '1.25rem', padding: '1rem 1.25rem' }}>
                <p className={styles.orderConfirmMeta}>Status: <strong>{cart?.cartStatus || 'Ordered'}</strong></p>
                {orderNumber ? (
                  <p className={styles.orderConfirmMeta}>Order Number: <strong>{orderNumber}</strong></p>
                ) : null}
                {orderId ? (
                  <p className={styles.orderConfirmMeta} style={{ marginBottom: 0 }}>Order ID: <strong>#{orderId}</strong></p>
                ) : null}
              </div>
              <DeliveryAddressCard address={address} title="Ship To" />
            </div>

            <div className={styles.checkoutAddressCard}>
              <h2 className={styles.orderSectionTitle}>Items Ordered</h2>
              <div className={styles.orderItemsList}>
                {items.map((item) => {
                  const imageUrl = item.productImage ? getImageSrc(item.productImage) : '';
                  return (
                    <div key={item.cartItemId ?? item.productId} className={styles.orderItemRow}>
                      <div className={styles.orderItemImage}>
                        {imageUrl ? <img src={imageUrl} alt={item.productName} /> : null}
                      </div>
                      <div className={styles.orderItemInfo}>
                        <p className={styles.orderItemName}>{item.productName}</p>
                        <p className={styles.orderItemMeta}>Qty: {item.quantity} × {formatPrice(item.price)}</p>
                      </div>
                      <div className={styles.orderItemTotal}>{formatPrice(calculateLineTotal(item))}</div>
                    </div>
                  );
                })}
              </div>

              <OrderTotalsSummary
                totals={totals}
                formatPrice={formatPrice}
                grandLabel="Amount Paid"
              />
            </div>
          </div>
        )}

        <div className={styles.orderConfirmActions}>
          <Button as={Link} to="/" variant="dark">Continue Shopping</Button>
          <Button as={Link} to="/account/orders" variant="outline-dark">View Order History</Button>
        </div>
      </Container>
    </section>
  );
};

export default OrderConfirmPage;
